import type { TeamCreationJobListResponse } from "@bezoned/shared/types/api/team/team-creation-job-list.types.js";
import type {
  TeamListQuery,
  TeamListResponse,
} from "@bezoned/shared/types/api/team/team-list.types.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import { acknowledgeTeamCreationJob } from "../../../services/api.service.js";
import {
  getTeamCreationJobsQueryOptions,
  teamCreationJobsQueryKey,
} from "./team-creation-jobs.query.js";
import {
  buildTeamLobbyRenderModel,
  isActiveCreateTeamStatus,
} from "./team-lobby-render-model.js";
import {
  getTeamsGetQueryKey,
  getTeamsGetQueryOptions,
} from "./teams-get.query.js";

type TeamCreationJobsData = TeamCreationJobListResponse;
const JOBS_POLL_INTERVAL_MS = 5000;
const EMPTY_JOBS: TeamCreationJobsData = [];
const EMPTY_TEAMS: TeamListResponse["teams"] = [];

const getTeamsFromQueryData = (data: TeamListResponse | undefined) =>
  data?.teams ?? EMPTY_TEAMS;

export function useTeamCreationLobby(
  editMode: boolean,
  teamQuery: TeamListQuery
) {
  const queryClient = useQueryClient();
  const previousStatusesRef = useRef<Map<string, string>>(new Map());
  const teamsQueryKey = getTeamsGetQueryKey(teamQuery);

  const teamsQuery = useQuery(getTeamsGetQueryOptions(teamQuery));
  const jobsQuery = useQuery({
    ...getTeamCreationJobsQueryOptions(),
    refetchInterval: (query) => {
      const jobs = query.state.data ?? [];
      const teams = getTeamsFromQueryData(
        queryClient.getQueryData(teamsQueryKey)
      );

      return buildTeamLobbyRenderModel({
        editMode: false,
        jobs,
        teams,
      }).shouldPollJobs
        ? JOBS_POLL_INTERVAL_MS
        : false;
    },
  });

  const teams = teamsQuery.data?.teams ?? EMPTY_TEAMS;
  const jobs = jobsQuery.data ?? EMPTY_JOBS;
  const hasJobsData = jobsQuery.data !== undefined;
  const jobsFetchMarker = `${jobsQuery.dataUpdatedAt}:${jobs.length}`;

  const renderModel = useMemo(
    () =>
      buildTeamLobbyRenderModel({
        editMode,
        jobs,
        teams,
      }),
    [editMode, jobs, teams]
  );

  useEffect(() => {
    if (!hasJobsData || jobsFetchMarker.length === 0) {
      return;
    }

    const previousStatuses = previousStatusesRef.current;
    const hasTransitionedToTerminalStatus = jobs.some((job) => {
      const previousStatus = previousStatuses.get(job.jobId);

      return (
        previousStatus !== undefined &&
        isActiveCreateTeamStatus(previousStatus as typeof job.status) &&
        !isActiveCreateTeamStatus(job.status)
      );
    });

    previousStatusesRef.current = new Map(
      jobs.map((job) => [job.jobId, job.status])
    );

    if (
      !hasTransitionedToTerminalStatus &&
      renderModel.unmatchedSuccessfulJobIds.size === 0
    ) {
      return;
    }

    const invalidateTeams = async () => {
      try {
        await queryClient.invalidateQueries({
          queryKey: teamsQueryKey,
        });
      } catch {
        // Ignore invalidation races caused by rapid polling/state changes.
      }
    };

    void invalidateTeams();
  }, [
    hasJobsData,
    jobs,
    jobsFetchMarker,
    queryClient,
    renderModel.unmatchedSuccessfulJobIds.size,
    teamsQueryKey,
  ]);

  const dismissFailedJobMutation = useMutation<
    Awaited<ReturnType<typeof acknowledgeTeamCreationJob>>,
    unknown,
    { jobId: string },
    { previousJobs: TeamCreationJobsData | undefined }
  >({
    mutationFn: ({ jobId }: { jobId: string }) =>
      acknowledgeTeamCreationJob({ jobId }),
    onError: (_error, _variables, context) => {
      if (context?.previousJobs) {
        queryClient.setQueryData(
          teamCreationJobsQueryKey,
          context.previousJobs
        );
      }
    },
    onMutate: async ({ jobId }) => {
      await queryClient.cancelQueries({ queryKey: teamCreationJobsQueryKey });

      const previousJobs = queryClient.getQueryData(
        teamCreationJobsQueryKey
      ) as TeamCreationJobsData | undefined;

      queryClient.setQueryData(
        teamCreationJobsQueryKey,
        (current: TeamCreationJobsData | undefined) =>
          (current ?? []).filter((job) => job.jobId !== jobId)
      );

      return { previousJobs };
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: teamCreationJobsQueryKey,
      });
    },
  });

  const dismissFailedJob = (jobId: string) => {
    dismissFailedJobMutation.mutate({ jobId });
  };

  return {
    dismissFailedJob,
    dismissingJobId: dismissFailedJobMutation.variables?.jobId ?? null,
    jobsQuery,
    renderModel,
    teamsQuery,
  };
}
